import clsx from 'clsx';
import { forwardRef, type ReactElement, type Ref } from 'react';
import { FormattedMessage } from 'react-intl';

import * as styles from './DataTableSelectionChip.sty.js';
import { DataTestId } from '../../../../core/types/data-props.js';
import { StylingProps } from '../../../../core/types/styling-props.js';
import { WithChildren } from '../../../../core/types/with-children.js';
import { canReceiveDefaultTextFormat as _canReceiveDefaultTextFormat } from '../../../../core/utils/can-receive-default-text-format.js';
import { Flex } from '../../../../layouts/flex/Flex.js';
import { Text } from '../../../../typography/text/Text.js';
import { getDataTableBorderWidth } from '../../../utils/data-table-border-width.js';
import { useDataTable } from '../../contexts/DataTableContext.js';
import { dataTableMessages } from '../../messages.js';

/**
 * @public
 */
export type DataTableSelectionChipProps = WithChildren &
  StylingProps &
  DataTestId;

/**
 *
 * @public
 */
export const DataTableSelectionChip = forwardRef(
  (
    props: DataTableSelectionChipProps,
    forwardedRef: Ref<HTMLDivElement>,
  ): ReactElement | null => {
    const {
      children,
      'data-testid': dataTestId,
      className: consumerClassName,
      style: consumerStyle,
      ...remainingProps
    } = props;

    const table = useDataTable();
    const rowsCount = table.getSelectionCount();
    const { rowDensity, contained } = table.getVisualVariantOptions();

    return (
      <Flex
        ref={forwardedRef}
        className={styles.selectionChipWrapper[rowDensity]}
        style={{ marginLeft: contained ? getDataTableBorderWidth() : 0 }}
      >
        <Flex
          data-testid={dataTestId}
          className={clsx([styles.selectionChip, consumerClassName])}
          style={consumerStyle}
          {...remainingProps}
        >
          {rowsCount}
        </Flex>
        {!_canReceiveDefaultTextFormat(children) ? (
          children
        ) : (
          <Text as="p" textStyle="base">
            {children ?? (
              <FormattedMessage {...dataTableMessages.selectedTotalRows} />
            )}
          </Text>
        )}
      </Flex>
    );
  },
);

(
  DataTableSelectionChip as typeof DataTableSelectionChip & {
    displayName: string;
  }
).displayName = 'DataTable.SelectionChip';
